import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  LocalCarWash as WashIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Power as PowerIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getCarWashInfo,
  updateCarWash,
} from "../../../../services/carWash.service";
import Slider from "react-slick";
import { ICarWashObject } from "../../../../types/carWash";

type Props = {};

const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  arrows: false,
  autoplay: true,
  gap: 10,
  autoplaySpeed: 3000,
};

const vehicleTypes = [
  { key: "sedan", labelKey: "vehicle.sedan", icon: "🚗" },
  { key: "cuv", labelKey: "vehicle.cuv", icon: "🚙" },
  { key: "suv", labelKey: "vehicle.suv", icon: "🚐" },
  { key: "minivan", labelKey: "vehicle.minivan", icon: "🚐" },
  { key: "van", labelKey: "vehicle.van", icon: "🚐" },
];

const serviceTypes = [
  { key: "inner", labelKey: "vehicle.innerWash" },
  { key: "outer", labelKey: "vehicle.outerWash" },
  { key: "both", labelKey: "vehicle.fullWash" },
];

const chipColor = (c?: string | null) =>
  c === "econom"
    ? ("success" as const)
    : c === "standard"
      ? ("info" as const)
      : c === "premium"
        ? ("warning" as const)
        : ("default" as const);

const CarWashInfo = (props: Props) => {
  const { t } = useTranslation();
  const [carWashInfo, setCarWashInfo] = useState<ICarWashObject | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<ICarWashObject | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [idCopied, setIdCopied] = useState(false);
  const { carWashID } = useParams();

  // Copy-to-clipboard for the record's _id — replaces the Unique ID table
  // column dropped from ManageCarWashes. navigator.clipboard.writeText
  // rejects on insecure origins / denied permission, so the rejection is
  // surfaced through the existing snackbar rather than failing silently.
  const handleCopyId = async () => {
    if (!carWashInfo?._id) return;
    try {
      await navigator.clipboard.writeText(carWashInfo._id);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: t("common.copyIdFailed"),
        severity: "error",
      });
    }
  };

  useEffect(() => {
    const handleGetCarWash = async () => {
      if (carWashID) {
        try {
          const res = await getCarWashInfo(carWashID);
          setCarWashInfo(res);
          setEditedData(res);
        } catch (error: any) {
          setSnackbar({
            open: true,
            message: error.message || t("carWashes.fetchFailed"),
            severity: "error",
          });
        }
      }
    };

    handleGetCarWash();
    // Re-runs only when carWashID changes; `t` changes identity on
    // language switch, and re-running this fetch because the UI language
    // changed is not wanted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carWashID]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset to original data
      setEditedData(carWashInfo);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!editedData || !carWashID) return;

    setLoading(true);
    try {
      await updateCarWash(carWashID, editedData);
      setCarWashInfo(editedData);
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: t("carWashes.updatedSuccess"),
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || t("carWashes.updateFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setEditedData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleServicePriceChange = (
    vehicleType: string,
    serviceType: string,
    value: number,
  ) => {
    setEditedData((prev: any) => ({
      ...prev,
      services: {
        ...prev.services,
        [vehicleType]: {
          ...prev.services[vehicleType],
          [serviceType]: value,
        },
      },
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getAveragePrice = (vehicleType: string) => {
    if (
      !carWashInfo?.services[vehicleType as keyof typeof carWashInfo.services]
    )
      return 0;
    const services =
      carWashInfo.services[vehicleType as keyof typeof carWashInfo.services];
    return (services.inner + services.outer + services.both) / 3;
  };

  if (!carWashInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography>{t("carWashes.loadingInfo")}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3} borderRadius={2} boxShadow={3} bgcolor="background.paper">
      {/* Header with Edit Toggle */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <BusinessIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" gutterBottom>
            {carWashInfo?.legalName}
          </Typography>
          <Tooltip title={idCopied ? t("common.idCopied") : t("common.copyId")}>
            <IconButton size="small" onClick={handleCopyId} sx={{ ml: 1 }}>
              {idCopied ? (
                <CheckIcon fontSize="small" color="success" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          {isEditing ? (
            <>
              <IconButton
                onClick={handleSave}
                disabled={loading}
                color="primary"
                title={t("common.saveChanges")}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={handleEditToggle}
                disabled={loading}
                color="secondary"
                title={t("common.cancelEditing")}
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={handleEditToggle}
              color="primary"
              title={t("carWashes.editInfo")}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{t("carWashes.basicInfo")}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      label={t("common.legalName")}
                      value={editedData?.legalName || ""}
                      onChange={(e) =>
                        handleInputChange("legalName", e.target.value)
                      }
                      margin="dense"
                    />
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("common.legalName")}:</strong>{" "}
                      {carWashInfo?.legalName}
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  {isEditing ? (
                    <Box>
                      <Typography variant="body2" mb={0.5}>
                        <strong>{t("carWashes.vehicleTypesSupported")}</strong>
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={(editedData?.vehicleTypes as string[]) ?? []}
                          onChange={(e) =>
                            handleInputChange("vehicleTypes", e.target.value)
                          }
                          renderValue={(selected) =>
                            (selected as string[]).length === 0
                              ? t("vehicle.allTypes")
                              : (selected as string[])
                                  .map((vt) => t(`vehicle.${vt}`))
                                  .join(", ")
                          }
                          displayEmpty
                        >
                          <MenuItem disabled value="">
                            <em>{t("carWashes.selectVehicleTypesHint")}</em>
                          </MenuItem>
                          {vehicleTypes.map((vt) => (
                            <MenuItem key={vt.key} value={vt.key}>
                              {t(vt.labelKey)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ) : (
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("carWashes.vehicleTypes")}:</strong>{" "}
                      {carWashInfo?.vehicleTypes &&
                      carWashInfo.vehicleTypes.length > 0
                        ? carWashInfo.vehicleTypes
                            .map((vt) => t(`vehicle.${vt}`))
                            .join(", ")
                        : t("vehicle.allTypes")}
                    </Typography>
                  )}
                </Grid>

                <Grid size={6}>
                  <Box display="flex" alignItems="center">
                    <StarIcon color="warning" sx={{ mr: 0.5 }} />
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("carWashTable.rating")}:</strong>{" "}
                      {carWashInfo?.rating || 0} / 5
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={6}>
                  {isEditing ? (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editedData?.active !== false}
                          onChange={(e) =>
                            handleInputChange("active", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center">
                          <PowerIcon
                            color={
                              editedData?.active !== false
                                ? "success"
                                : "disabled"
                            }
                            sx={{ mr: 0.5 }}
                          />
                          <Typography variant="body1">
                            <strong>{t("carWashes.operatingStatus")}:</strong>{" "}
                            {editedData?.active !== false
                              ? t("status.active")
                              : t("status.inactive")}
                          </Typography>
                        </Box>
                      }
                    />
                  ) : (
                    <Box display="flex" alignItems="center">
                      <PowerIcon
                        color={
                          carWashInfo?.active !== false ? "success" : "disabled"
                        }
                        sx={{ mr: 0.5 }}
                      />
                      <Typography variant="body1" gutterBottom>
                        <strong>{t("carWashes.operatingStatus")}:</strong>
                        <Chip
                          label={
                            carWashInfo?.active !== false
                              ? t("status.active")
                              : t("status.inactive")
                          }
                          size="small"
                          color={
                            carWashInfo?.active !== false
                              ? "success"
                              : "default"
                          }
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Owner Information */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {t("carWashes.ownerInformation")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("common.name")}:</strong>{" "}
                    {carWashInfo?.owner.firstName} {carWashInfo?.owner.lastName}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Box display="flex" alignItems="center">
                    <PhoneIcon color="primary" sx={{ mr: 0.5 }} />
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("common.phone")}:</strong>{" "}
                      {carWashInfo?.owner.country_code}{" "}
                      {carWashInfo?.owner.phone}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("carWashDetails.contactPerson")}:</strong>{" "}
                    {carWashInfo?.contact.fullName}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Box display="flex" alignItems="center">
                    <PhoneIcon color="primary" sx={{ mr: 0.5 }} />
                    <Typography variant="body1" gutterBottom>
                      <strong>{t("carWashes.contactPhone")}:</strong>{" "}
                      {carWashInfo?.contact.phone}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Washing Class — full width under Basic Information and Owner Information */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <WashIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {t("carWashes.washingClass")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {carWashInfo?.washClasses &&
              carWashInfo.washClasses.length > 0 ? (
                <Box
                  mt={0.5}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {(["sedan", "cuv", "suv", "minivan", "van"] as const).map(
                    (vt) => {
                      const vtEntries = carWashInfo.washClasses!.filter(
                        (e) => e.vehicleType === vt && e.washClass !== null,
                      );
                      if (vtEntries.length === 0) return null;
                      return (
                        <Box
                          key={vt}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ minWidth: 60, fontWeight: 600 }}
                          >
                            {t(`vehicle.${vt}`)}:
                          </Typography>
                          {vtEntries.map((e) => (
                            <Box
                              key={`${e.vehicleType}-${e.serviceType}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="caption">
                                {t(
                                  `vehicle.service${e.serviceType
                                    .charAt(0)
                                    .toUpperCase()}${e.serviceType.slice(1)}`,
                                )}
                              </Typography>
                              <Chip
                                label={t(`washClass.${e.washClass}`)}
                                color={chipColor(e.washClass)}
                                size="small"
                              />
                            </Box>
                          ))}
                        </Box>
                      );
                    },
                  )}
                </Box>
              ) : (
                <Chip
                  label={t("washClass.unclassified")}
                  color="default"
                  size="small"
                  variant="outlined"
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Location Information */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {t("carWashes.locationInformation")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("common.address")}</strong>{" "}
                    {carWashInfo?.location?.street ||
                      (carWashInfo?.location as any)?.streetName ||
                      "N/A"}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("common.description")}:</strong>{" "}
                    {carWashInfo?.location.description ||
                      t("carWashes.noDescription")}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{t("common.latitude")}:</strong>{" "}
                    {carWashInfo?.location.latitude}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{t("common.longitude")}:</strong>{" "}
                    {carWashInfo?.location.longitude}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Prices */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <MoneyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {t("carWashDetails.servicePrices")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2} className="flex flex-col sm:flex-col">
                {vehicleTypes.map((type) => (
                  <Grid size={{ sm: 2.4, xs: 6 }} key={type.key}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent sx={{ textAlign: "center", p: 2 }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                          {type.icon}
                        </Typography>
                        <Typography variant="subtitle1" gutterBottom>
                          {t(type.labelKey)}
                        </Typography>

                        {isEditing ? (
                          <>
                            {serviceTypes.map((service) => (
                              <TextField
                                key={service.key}
                                fullWidth
                                size="small"
                                label={t(service.labelKey)}
                                type="number"
                                value={
                                  editedData?.services[
                                    type.key as keyof typeof editedData.services
                                  ]?.[
                                    service.key as keyof typeof editedData.services.sedan
                                  ] || 0
                                }
                                onChange={(e) =>
                                  handleServicePriceChange(
                                    type.key,
                                    service.key,
                                    Number(e.target.value),
                                  )
                                }
                                margin="dense"
                                InputProps={{
                                  startAdornment: (
                                    <Typography variant="caption">₼</Typography>
                                  ),
                                }}
                              />
                            ))}
                          </>
                        ) : (
                          <>
                            {serviceTypes.map((service) => (
                              <Typography
                                key={service.key}
                                variant="body2"
                                gutterBottom
                              >
                                {t(service.labelKey)}: ₼
                                {carWashInfo?.services[
                                  type.key as keyof typeof carWashInfo.services
                                ]?.[
                                  service.key as keyof typeof carWashInfo.services.sedan
                                ] || 0}
                              </Typography>
                            ))}
                            <Chip
                              label={t("carWashes.averagePrice", {
                                value: getAveragePrice(type.key).toFixed(2),
                              })}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 1 }}
                            />
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Image Gallery */}
        {carWashInfo?.images && carWashInfo.images.length > 0 && (
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <WashIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    {t("carWashes.imageGallery")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {carWashInfo.images.length === 1 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box
                      component="img"
                      src={carWashInfo.images[0]}
                      alt={t("carWashes.imageAlt")}
                      sx={{
                        width: "100%",
                        maxWidth: 600,
                        height: 300,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                ) : (
                  <Slider
                    {...sliderSettings}
                    infinite={carWashInfo.images.length > 3}
                    slidesToShow={Math.min(3, carWashInfo.images.length)}
                    autoplay={carWashInfo.images.length > 3}
                  >
                    {carWashInfo.images.map((imgUrl: string, idx: number) => (
                      <Box key={idx} px={1}>
                        <Box
                          component="img"
                          src={imgUrl}
                          alt={t("carWashes.imageAltIndexed", {
                            index: idx + 1,
                          })}
                          sx={{
                            width: "100%",
                            height: 250,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    ))}
                  </Slider>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Additional Information */}
        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CalendarIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {t("carWashes.additionalInfo")}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("ownerInfo.created")}:</strong>{" "}
                    {new Date(
                      carWashInfo?.createdAt || "",
                    ).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>{t("carWashes.totalImages")}:</strong>{" "}
                    {carWashInfo?.images.length || 0}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{t("common.note")}:</strong>{" "}
                    {t("carWashes.editNote")}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CarWashInfo;
